import { Menu } from '../primitives';
import type { MenuItem } from '../primitives/Menu';
import type { GroupByOption } from './types';

export interface GroupByMenuProps {
  /** All group-by choices in the order they render. The `none` option is conventional. */
  options: ReadonlyArray<GroupByOption>;
  /** Currently selected option key. */
  current: string;
  onChange: (next: string) => void;
  /**
   * Keys that should render disabled (e.g. `'status'` on the Work Items Board
   * tab where columns already group by status). Default: no disabled options.
   */
  disabledKeys?: ReadonlyArray<string>;
  /** Test-id prefix so each host page gets stable selectors. */
  testIdPrefix: string;
}

/**
 * `Group by ▾` — single-select dropdown. The selected option shows a `✓`.
 * Pages that want to hide certain options entirely should omit them from
 * `options`; pages that want to disable an option pass it in `disabledKeys`.
 */
export function GroupByMenu({
  options,
  current,
  onChange,
  disabledKeys,
  testIdPrefix
}: GroupByMenuProps) {
  const currentLabel =
    options.find((o) => o.key === current)?.label ??
    options[0]?.label ??
    'None';
  const disabled = disabledKeys ?? [];

  const items: MenuItem[] = options.map((opt) => {
    const selected = opt.key === current;
    const isDisabled = disabled.includes(opt.key);
    return {
      id: opt.key,
      'data-testid': `${testIdPrefix}-groupby-item-${opt.key}`,
      disabled: isDisabled,
      label: (
        <span className="pn-listctl-menu-checkrow">
          <span className="pn-listctl-menu-check" aria-hidden="true">
            {selected ? '✓' : ''}
          </span>
          <span>{opt.label}</span>
        </span>
      ),
      onSelect: isDisabled ? undefined : () => onChange(opt.key)
    };
  });

  return (
    <Menu
      trigger={(api) => (
        <button
          type="button"
          className="pn-btn pn-btn-sm"
          data-testid={`${testIdPrefix}-groupby-trigger`}
          {...api}
        >
          Group by: {currentLabel} ▾
        </button>
      )}
      items={items}
      label="Group by"
      align="end"
      data-testid={`${testIdPrefix}-groupby-menu`}
    />
  );
}
